import { PaneResizeHandle } from '@/components/ui/pane-resize-handle'
import type { FormPreviewMode, FormPreviewSnapshot } from '@/contracts/form-preview'
import { useResizablePane } from '@/hooks/use-resizable-pane'
import { getTwoPaneGridTemplate, previewPaneDefaults } from '@/lib/resizable-panes'
import { useFormPlayerStore } from '@/stores/formPlayerStore'
import type { AnswerValue } from '@/types/form'
import { type CSSProperties, useState } from 'react'
import { FormPreviewStepNavigator } from './FormPreviewStepNavigator'
import { type FormPreviewDevice, FormPreviewToolbar } from './FormPreviewToolbar'
import { PreviewPlayerAdapter } from './PreviewPlayerAdapter'

export function FormPreviewShell({
  snapshot,
  mode,
  device: controlledDevice,
  onDeviceChange,
  showToolbar = true,
  showStepNavigator = true,
  showOnePageSubmitBar = true,
}: {
  snapshot: FormPreviewSnapshot
  mode?: FormPreviewMode
  scenarioAnswers?: Array<[string, AnswerValue]>
  device?: FormPreviewDevice
  onDeviceChange?: (device: FormPreviewDevice) => void
  showToolbar?: boolean
  showStepNavigator?: boolean
  showOnePageSubmitBar?: boolean
}) {
  const [internalDevice, setInternalDevice] = useState<FormPreviewDevice>('desktop')
  const {
    width: stepNavWidth,
    collapsed: stepNavCollapsed,
    startResize: startStepNavResize,
    toggleCollapsed: toggleStepNavCollapsed,
  } = useResizablePane({
    initialWidth: 280,
    config: previewPaneDefaults.nav,
  })
  const device = controlledDevice ?? internalDevice
  const phase = useFormPlayerStore((state) => state.phase)
  const currentQuestion = useFormPlayerStore((state) => state.questions[state.currentIndex])
  const load = useFormPlayerStore((state) => state.load)
  const activeMode = mode ?? snapshot.mode
  const initialPhase = snapshot.steps.some((step) => step.kind === 'welcome')
    ? 'welcome'
    : 'questions'
  const activeStepId =
    phase === 'welcome'
      ? 'welcome'
      : phase === 'thankyou'
        ? 'completion'
        : currentQuestion
          ? `question:${currentQuestion.id}`
          : 'welcome'

  function restartPreview() {
    load(
      {
        ...snapshot.form,
        groups: snapshot.groups,
      } as typeof snapshot.form & { groups: typeof snapshot.groups },
      { mode: 'preview', initialPhase },
    )
  }

  function changeDevice(nextDevice: FormPreviewDevice) {
    setInternalDevice(nextDevice)
    onDeviceChange?.(nextDevice)
  }

  const previewGridColumns = getTwoPaneGridTemplate({
    navWidth: stepNavWidth,
    navCollapsed: stepNavCollapsed,
    contentMinWidth: 0,
    navConfig: previewPaneDefaults.nav,
  })

  return (
    <section className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
      {showToolbar && (
        <FormPreviewToolbar
          title={snapshot.form.title}
          source={snapshot.source}
          mode={activeMode}
          device={device}
          onDeviceChange={changeDevice}
          onRestart={restartPreview}
        />
      )}
      <div
        data-testid="form-preview-grid"
        className={
          showStepNavigator
            ? 'grid min-h-0 flex-1 grid-cols-1 overflow-hidden transition-[grid-template-columns] duration-200 ease-out lg:grid-cols-[var(--form-preview-grid-columns)]'
            : 'grid min-h-0 flex-1 grid-cols-1 overflow-hidden'
        }
        style={
          showStepNavigator
            ? ({ '--form-preview-grid-columns': previewGridColumns } as CSSProperties)
            : undefined
        }
      >
        {showStepNavigator ? (
          <div className="relative min-h-0 min-w-0 overflow-hidden">
            <div
              className={
                stepNavCollapsed
                  ? 'h-full pointer-events-none opacity-0 transition-opacity duration-150'
                  : 'h-full transition-opacity duration-150'
              }
              aria-hidden={stepNavCollapsed}
            >
              <FormPreviewStepNavigator steps={snapshot.steps} activeStepId={activeStepId} />
            </div>
            <PaneResizeHandle
              edge="right"
              collapsed={stepNavCollapsed}
              resizeLabel="拖曳調整預覽步驟左欄寬度"
              collapseLabel="收合預覽步驟左欄"
              expandLabel="展開預覽步驟左欄"
              onResizeStart={startStepNavResize}
              onToggle={toggleStepNavCollapsed}
            />
          </div>
        ) : null}
        <main
          className="min-h-0 overflow-y-auto bg-slate-100 p-4"
          data-testid="form-preview-main-scroll"
        >
          <div
            className={
              device === 'mobile'
                ? 'mx-auto h-[760px] max-h-full w-[390px] max-w-full overflow-y-auto rounded-[28px] border border-slate-300 bg-white shadow-xl'
                : 'mx-auto h-full min-h-full w-full overflow-visible rounded-lg border border-slate-300 bg-white shadow-xl'
            }
          >
            <PreviewPlayerAdapter
              snapshot={snapshot}
              showOnePageSubmitBar={showOnePageSubmitBar}
            />
          </div>
        </main>
      </div>
    </section>
  )
}
